import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Combobox from "@/components/Combobox";
import DatePicker from "@/components/DatePicker";
import { usd, shortDate } from "@/lib/format";
import {
  Zap, Database, ArrowRight, Award, TrendingUp, TrendingDown,
  Calendar, Ship, Anchor, Sparkles, Search, Info, ShieldCheck,
} from "lucide-react";

const DIRECTIONS = [
  { value: "SB", label: "Southbound — Overseas → Australia", icon: "↓" },
  { value: "NB", label: "Northbound — Australia → Overseas", icon: "↑" },
  { value: "AUNZ", label: "Trans-Tasman — AU ↔ NZ", icon: "↔" },
  { value: "COASTAL", label: "Coastal — within Australia", icon: "↻" },
];

const SIZES = [
  { value: "20GP", label: "20'GP" },
  { value: "40GP", label: "40'GP" },
  { value: "40HC", label: "40'HC" },
];

const rateKey = (size) =>
  size === "20GP" ? "allin_sell_20" : size === "40GP" ? "allin_sell_40" : "allin_sell_40hq";

const buyKey = (size) =>
  size === "20GP" ? "allin_buy_20" : size === "40GP" ? "allin_buy_40" : "allin_buy_40hq";

const CARRIER_COLORS = {
  MAERSK: "#42B0D5", MSC: "#053D7C", CMA: "#0066B2", "CMA CGM": "#0066B2",
  ONE: "#FF1E1E", COSCO: "#D7141A", EVERGREEN: "#0E8E3E", HAPAG: "#FF6900",
  OOCL: "#005EB8", ZIM: "#FF6600", PIL: "#1F4E79", ANL: "#003E7E",
  YML: "#0086C7", HMM: "#003A70", "TS LINES": "#E30613", TSLINES: "#E30613",
  NPDL: "#00A4A6", NAUTICAL: "#1F4E79", QUAY: "#888", SEALEAD: "#888",
};

export default function CargopticPricing() {
  const { user } = useAuth();
  const realAdmin = user?.role === "ADMIN";
  const [viewAsCustomer, setViewAsCustomer] = useState(false);
  const isAdmin = realAdmin && !viewAsCustomer;
  const [stats, setStats] = useState(null);
  const [direction, setDirection] = useState("SB");
  const [size, setSize] = useState("40HC");
  const [pol, setPol] = useState("");
  const [pod, setPod] = useState("");
  const [validOn, setValidOn] = useState("");
  const [pols, setPols] = useState([]);
  const [pods, setPods] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cargoptic-pricing/stats").then((r) => setStats(r.data));
  }, []);

  // Load POL list when direction changes
  useEffect(() => {
    setPol(""); setPod(""); setResult(null);
    api.get("/cargoptic-pricing/ports", { params: { direction, role: "pol" } }).then((r) => setPols(r.data));
  }, [direction]);

  // Cascading POD list when POL changes
  useEffect(() => {
    if (!pol) { setPods([]); return; }
    api.get("/cargoptic-pricing/lanes", { params: { direction, pol } }).then((r) => setPods(r.data));
  }, [direction, pol]);

  const runQuote = async () => {
    if (!pol || !pod) return;
    setBusy(true);
    try {
      const r = await api.get("/cargoptic-pricing/quote", {
        params: { direction, pol, pod, size, valid_on: validOn || undefined },
      });
      setResult(r.data);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setResult(null);
  }, [pol, pod, size, validOn, direction]);

  const summary = result?.summary;
  const rows = result?.rows || [];
  const maxRate = useMemo(() => {
    const k = rateKey(size);
    return Math.max(...rows.map((r) => r[k] || 0), 1);
  }, [rows, size]);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="cargoptic-pricing-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">WeDelvr Pricing — Instant Quote</h1>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-[#16A34A] text-white">Live FAK Rates</span>
            {isAdmin && <span data-testid="admin-margin-badge" className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Admin · Margin view</span>}
            {realAdmin && viewAsCustomer && <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]">Previewing as customer</span>}
          </div>
          <p className="text-sm text-zinc-500 mt-1">{isAdmin && <span className="text-[#991B1B]">BUY rates &amp; margin visible (admin only).</span>}</p>
          {realAdmin && (
            <button
              data-testid="view-as-customer-toggle"
              onClick={() => setViewAsCustomer((v) => !v)}
              className="mt-2 text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors inline-flex items-center gap-1.5"
            >
              <ShieldCheck className="w-3 h-3" />
              {viewAsCustomer ? "Switch back to Admin view" : "Preview as Customer"}
            </button>
          )}
        </div>
      </div>

      {/* Filter panel — like a contribution margin tool */}
      <div className="bg-white border border-zinc-200 p-5" data-testid="filter-panel">
        <div className="data-label mb-4 flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#002FA7]" />Quote parameters</div>

        {/* Direction tabs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-5">
          {DIRECTIONS.map((d) => (
            <button
              key={d.value}
              data-testid={`dir-${d.value}`}
              onClick={() => setDirection(d.value)}
              className={`bg-white p-3 text-left hover:bg-zinc-50/60 transition-colors ${direction === d.value ? "border-t-2 border-t-[#002FA7] bg-[#E6EBF6]" : ""}`}
            >
              <div className="metric text-xs text-zinc-400 mb-1">{d.icon}</div>
              <div className="text-xs font-semibold leading-tight">{d.label}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="data-label">Port of Loading</Label>
            <Combobox
              testId="cp-pol"
              value={pol}
              onChange={setPol}
              options={pols}
              placeholder="Select POL..."
              emptyText="No ports match."
            />
            <div className="text-[10px] text-zinc-400 mt-1 metric">{pols.length} ports</div>
          </div>
          <div>
            <Label className="data-label">Port of Discharge</Label>
            <Combobox
              testId="cp-pod"
              value={pod}
              onChange={setPod}
              options={pods}
              placeholder={pol ? "Select POD..." : "Choose POL first"}
              emptyText="No matching destinations."
              disabled={!pol}
            />
            <div className="text-[10px] text-zinc-400 mt-1 metric">{pods.length} destinations</div>
          </div>
          <div>
            <Label className="data-label">Container Size</Label>
            <Combobox
              testId="cp-size"
              value={size}
              onChange={setSize}
              options={SIZES}
              placeholder="Select size..."
            />
          </div>
          <div>
            <Label className="data-label">Valid On</Label>
            <DatePicker testId="cp-validon" value={validOn} onChange={setValidOn} placeholder="any date" />
          </div>
          <Button
            data-testid="cp-search"
            onClick={runQuote}
            disabled={!pol || !pod || busy}
            className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-10"
          ><Search className="w-4 h-4 mr-2" />{busy ? "..." : "Quote"}</Button>
          <Button
            data-testid="cp-reset"
            onClick={() => { setPol(""); setPod(""); setSize("40HC"); setValidOn(""); setResult(null); }}
            variant="outline"
            className="rounded-none h-10 border-zinc-300 hover:border-[#991B1B] hover:text-[#991B1B]"
          >Reset</Button>
        </div>
      </div>

      {/* Output */}
      {!pol || !pod ? (
        <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="cp-empty">
          <Ship className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Select a Port of Loading and Port of Discharge above to see live FAK rates.</p>
          {direction === "SB" && (
            <p className="text-xs text-zinc-400 mt-3 metric">
              Try: SHANGHAI → SYDNEY · NINGBO → MELBOURNE · QINGDAO → BRISBANE
            </p>
          )}
        </div>
      ) : !result ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">Loading rates...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="cp-norows">
          <Info className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-700 font-medium">No FAK rates found for this combination.</p>
          <p className="text-xs text-zinc-500 mt-2">Try a different validity date or container size.</p>
        </div>
      ) : (
        <>
          {/* Headline */}
          <div className="bg-white border border-zinc-200 p-5" data-testid="cp-headline">
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-semibold flex items-center gap-2">
                <Anchor className="w-4 h-4 text-[#002FA7]" />
                <span className="metric">{pol}</span>
                <ArrowRight className="w-4 h-4 text-zinc-400" />
                <span className="metric">{pod}</span>
                <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 bg-zinc-100 border border-zinc-200 ml-2">{size}</span>
              </div>
              <div className="text-xs text-zinc-500">
                {summary.carrier_count} carriers · {result.all_rows_count} rate rows
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
              <div className="bg-white p-4 border-l-[3px] border-l-[#16A34A]">
                <div className="data-label text-[#166534]">Cheapest All-in</div>
                <div className="metric text-3xl mt-2">{usd(summary.low)}</div>
                <div className="text-xs text-zinc-500 mt-1">by {summary.cheapest_carrier}</div>
              </div>
              <div className="bg-white p-4 border-l-[3px] border-l-[#002FA7]">
                <div className="data-label text-[#002FA7]">Market Average</div>
                <div className="metric text-3xl mt-2">{usd(summary.avg)}</div>
              </div>
              <div className="bg-white p-4 border-l-[3px] border-l-[#991B1B]">
                <div className="data-label text-[#991B1B]">Most Expensive</div>
                <div className="metric text-3xl mt-2">{usd(summary.high)}</div>
              </div>
              <div className="bg-white p-4">
                <div className="data-label">Spread</div>
                <div className="metric text-3xl mt-2">{summary.spread_pct}%</div>
                <div className="text-xs text-zinc-500 mt-1">save {usd(summary.high - summary.low)} vs worst</div>
              </div>
            </div>
          </div>

          {/* Carrier waterfall */}
          <div className="bg-white border border-zinc-200">
            <div className="px-5 py-4 border-b border-zinc-200">
              <h3 className="font-heading text-lg font-bold tracking-tight">Carrier Comparison — All-in SELL Rates ({size})</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Cheapest rate per carrier shown. Validity windows from master FAK sheet.</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  {["#", "Carrier", "Contract", "Service", "T/T", "Routing", "Valid From", "Valid To", "All-in SELL", ...(isAdmin ? ["All-in BUY", "Margin", "Margin %"] : []), "vs Cheapest", "Bar"].map((h) => (
                    <th key={h} className={`px-3 py-2.5 data-label ${["All-in BUY","Margin","Margin %"].includes(h) ? 'bg-[#FEE2E2] text-[#991B1B]' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="cp-rates-tbody">
                {rows.map((r, i) => {
                  const k = rateKey(size);
                  const bk = buyKey(size);
                  const rate = r[k];
                  const buy = r[bk];
                  const w = rate ? (rate / maxRate) * 100 : 0;
                  const vsLow = summary.low ? (((rate - summary.low) / summary.low) * 100).toFixed(0) : 0;
                  const color = CARRIER_COLORS[r.carrier] || "#71717A";
                  return (
                    <tr key={r.id} className={`border-b border-zinc-100 hover:bg-zinc-50/60 ${i === 0 ? "bg-[#F0FDF4]/40" : ""}`} data-testid={`cp-row-${i}`}>
                      <td className="px-3 py-2.5 metric text-xs text-zinc-400">{i + 1}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2 h-6" style={{ background: color }} />
                          <span className="font-heading font-semibold">{r.carrier}</span>
                          {i === 0 && <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 bg-[#16A34A] text-white ml-1"><Award className="w-2.5 h-2.5 inline" /> Best</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 metric text-[11px] text-zinc-500">{r.contract_no || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-zinc-600">{r.service || "—"}</td>
                      <td className="px-3 py-2.5 metric text-xs">{r.tt_days ? `${Math.round(r.tt_days)}d` : "—"}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {r.transhipment ? <span className={`text-[10px] uppercase tracking-[0.1em] ${r.transhipment.includes("DIRECT") ? "text-[#166534]" : "text-[#B45309]"}`}>{r.transhipment}{r.ts_port ? ` · ${r.ts_port}` : ""}</span> : "—"}
                      </td>
                      <td className="px-3 py-2.5 metric text-xs">{r.valid_from ? shortDate(r.valid_from) : "—"}</td>
                      <td className="px-3 py-2.5 metric text-xs">{r.valid_to ? shortDate(r.valid_to) : "—"}</td>
                      <td className="px-3 py-2.5 metric text-base font-semibold">{rate ? `${r.currency} ${rate.toLocaleString()}` : "—"}</td>
                      {isAdmin && (
                        <>
                          <td className="px-3 py-2.5 metric text-sm bg-[#FEE2E2]/30">{buy ? `${r.currency} ${buy.toLocaleString()}` : "—"}</td>
                          <td className={`px-3 py-2.5 metric text-sm bg-[#FEE2E2]/30 ${r.margin_usd > 0 ? 'text-[#166534]' : r.margin_usd < 0 ? 'text-[#991B1B]' : ''}`}>{r.margin_usd != null ? `${r.currency} ${r.margin_usd.toLocaleString()}` : "—"}</td>
                          <td className={`px-3 py-2.5 metric text-sm bg-[#FEE2E2]/30 font-semibold ${r.margin_pct >= 20 ? 'text-[#166534]' : r.margin_pct >= 5 ? 'text-[#B45309]' : r.margin_pct != null ? 'text-[#991B1B]' : ''}`}>{r.margin_pct != null ? `${r.margin_pct}%` : "—"}</td>
                        </>
                      )}
                      <td className="px-3 py-2.5 metric text-xs">
                        {i === 0 ? <span className="text-[#166534]">baseline</span> :
                          <span className={Number(vsLow) > 50 ? "text-[#991B1B]" : Number(vsLow) > 20 ? "text-[#B45309]" : "text-zinc-600"}>+{vsLow}%</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="h-2 bg-zinc-100 w-full max-w-[140px]">
                          <div className="h-full" style={{ width: `${w}%`, background: color }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Side-by-side 20/40/40HC for the cheapest */}
          <div className="bg-white border border-zinc-200 p-5" data-testid="cp-allsizes">
            <h3 className="font-heading text-lg font-bold tracking-tight mb-1">All Sizes — Best Rate</h3>
            <p className="text-xs text-zinc-500 mb-4">Lowest available rate across all carriers, per container size.</p>
            <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              {["20GP", "40GP", "40HC"].map((sz) => {
                const k = rateKey(sz);
                const valid = rows.filter((r) => r[k] && r[k] > 0).sort((a, b) => a[k] - b[k]);
                const best = valid[0];
                return (
                  <div key={sz} className="bg-white p-5">
                    <div className="data-label">{sz.replace("GP", "'GP").replace("HC", "'HC")}</div>
                    {best ? (
                      <>
                        <div className="metric text-3xl mt-2">{best.currency} {best[k].toLocaleString()}</div>
                        <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5" style={{ background: CARRIER_COLORS[best.carrier] || "#71717A" }} />
                          {best.carrier}
                          {best.tt_days && <span className="text-zinc-400 ml-2">{Math.round(best.tt_days)}d transit</span>}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-zinc-400 mt-3">no rate</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
